import { useEffect, useState } from "react";
import api, { fmtUSD, fmtPct } from "@/lib/api";
import { Panel, Table, Pct, Empty, StatusBadge } from "@/components/terminal";

export default function Crypto() {
  const [prices, setPrices] = useState(null);
  const [error, setError] = useState(null);
  const [positions, setPositions] = useState([]);
  const [assets, setAssets] = useState([]);
  const load = () => {
    setError(null);
    api.get("/market/prices").then((r) => setPrices(r.data)).catch((e) => setError("Market data unavailable. Check provider status."));
    api.get("/positions").then((r) => setPositions(r.data.positions)).catch(() => {});
    api.get("/dashboard").then((r) => setAssets(r.data.assets || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  if (!prices) {
    if (error) return (
      <div className="panel p-6" data-testid="crypto-error">
        <div className="text-red-400 text-sm mb-3">{error}</div>
        <button data-testid="crypto-retry" onClick={load} className="text-xs border border-[#D4AF37]/50 text-[#D4AF37] rounded-sm px-3 py-1.5">RETRY</button>
      </div>
    );
    return <div className="text-slate-500 font-mono text-sm" data-testid="crypto-loading">Loading market data…</div>;
  }
  return (
    <div className="space-y-5" data-testid="crypto-page">
      <h2 className="font-display text-xl font-bold">Crypto Markets & Positions</h2>
      <Panel title="Market Data (Crypto.com public feed, CoinGecko fallback)" testId="crypto-market-panel">
        <Table testId="crypto-market-table" head={[{ label: "Asset" }, { label: "State" }, { label: "Price", num: true }, { label: "24h", num: true }, { label: "7d", num: true }, { label: "30d", num: true }, { label: "90d", num: true }, { label: "90d High", num: true }, { label: "Source" }]}>
          {Object.values(prices).map((q) => {
            const asset = assets.find((a) => a.symbol === q.symbol);
            return (
              <tr key={q.symbol} className="hover:bg-[#222630]" data-testid={`market-row-${q.symbol}`}>
                <td className="td-cell font-semibold text-slate-100">{q.symbol}</td>
                <td className="td-cell"><StatusBadge status={asset?.state || "WATCHLIST"} /></td>
                <td className="td-num">{q.price ? fmtUSD(q.price) : "—"}</td>
                <td className="td-num"><Pct value={(q.change_24h || 0) * 100} /></td>
                <td className="td-num"><Pct value={q.change_7d != null ? q.change_7d * 100 : null} /></td>
                <td className="td-num"><Pct value={q.change_30d != null ? q.change_30d * 100 : null} /></td>
                <td className="td-num"><Pct value={q.change_90d != null ? q.change_90d * 100 : null} /></td>
                <td className="td-num">{q.high_90d ? fmtUSD(q.high_90d) : "—"}</td>
                <td className="td-cell text-slate-500">{q.source || "unavailable"}{q.stale ? " (stale)" : ""}</td>
              </tr>
            );
          })}
        </Table>
      </Panel>
      <Panel title="Open Crypto Positions" testId="crypto-positions-panel">
        {positions.length === 0 ? <Empty>No crypto positions. Treasury is 100% cash.</Empty> : (
          <Table testId="crypto-positions-table" head={[{ label: "Asset" }, { label: "Quantity", num: true }, { label: "Avg Cost", num: true }, { label: "Value", num: true }, { label: "Unrealized P/L", num: true }]}>
            {positions.map((p) => (
              <tr key={p.asset} className="hover:bg-[#222630]">
                <td className="td-cell font-semibold">{p.asset}</td>
                <td className="td-num">{p.quantity.toFixed(8)}</td>
                <td className="td-num">{fmtUSD(p.cost_basis / p.quantity)}</td>
                <td className="td-num">{fmtUSD(p.value)}</td>
                <td className="td-num"><Pct value={p.cost_basis ? (p.unrealized_pl / p.cost_basis) * 100 : 0} /></td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
    </div>
  );
}
